import React from 'react';

import clsxm from '@/lib/clsxm';
import useBreakpoints from '@/hooks/use-breakpoints';
import { useGlobalContext } from '@/hooks/use-global-context';
import useLogoUrl from '@/hooks/use-logo-url';

import AppStoreBadgeLink from '@/components/icons/app-store-badge-link';
import NomadLogoIcon from '@/components/icons/nomad-logo-icon';
import PlayStoreBadgeLink from '@/components/icons/play-store-badge-link';
import Link, { LinkVariant } from '@/components/link';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TikTokIcon,
} from '@/components/social-icons';
import Typography from '@/components/typography';

import { routes } from '@/utils/route-utils';

type LinkProps = { label: string; href?: string; target?: '_blank' };

export default function Footer() {
  const { templateSetting } = useGlobalContext().pageMeta;
  const includeLinks = !templateSetting?.isPPC;
  const logoUrl = useLogoUrl();
  const {
    topContent = null,
    linkContent = null,
    linkUrlPath = null,
  } = templateSetting?.footer || {};
  const {
    currentEntry: { storeBadgeScale, nomadLogoScale },
  } = useBreakpoints({
    sm: { storeBadgeScale: 1, nomadLogoScale: 2.5 },
    lg: { storeBadgeScale: 1.5, nomadLogoScale: 1.5 },
  });

  const linkColumns: Record<string, LinkProps[]>[] = [
    {
      About: [
        { label: 'Why Nomad?', href: '/why-nomad' },
        { label: 'Press', href: '/press' },
        { label: 'Careers', href: '/careers' },
        { label: 'Refer A Friend', href: '/referrals' },
        { label: 'About Us', href: '/about' },
      ],
      Resources: [
        { label: 'FAQs', href: '/faqs/getting-started' },
        {
          label: 'Support',
          href: 'https://faq.nomadhealth.com/',
          target: '_blank',
        },
        { label: 'Contact Us', href: '/contact-us' },
      ],
      Staffing: [{ label: 'Facilities', href: '/facilities' }],
    },
    {
      'Travel Jobs': [
        { label: 'Travel Nurse', href: '/healthcare-jobs/nurse/travel-nurse' },
        {
          label: 'Cath Lab Tech',
          href: '/healthcare-jobs/allied/travel-allied/cath-lab-tech',
        },
        {
          label: 'Laboratory',
          href: '/healthcare-jobs/allied/travel-allied/lab-tech',
        },
        {
          label: 'Occupational Therapist',
          href: '/healthcare-jobs/allied/travel-allied/occupational-therapist',
        },
        {
          label: 'Physical Therapist',
          href: '/healthcare-jobs/allied/travel-allied/physical-therapist',
        },
        {
          label: 'Radiology Tech',
          href: '/healthcare-jobs/allied/travel-allied/radiology-tech',
        },
        {
          label: 'Respiratory Therapist',
          href: '/healthcare-jobs/allied/travel-allied/respiratory-therapist',
        },
        {
          label: 'Speech Language Pathologist',
          href: '/healthcare-jobs/allied/travel-allied/speech-language-pathologist',
        },
        {
          label: 'Surgical Technologist',
          href: '/healthcare-jobs/allied/travel-allied/surgical-technologist',
        },
        {
          label: 'Ultrasound Technologist',
          href: '/healthcare-jobs/allied/travel-allied/ultrasound-technologist',
        },
      ],
    },
    {
      'Your Account': [
        { label: 'Join Us', href: routes.register() },
        { label: 'Sign In', href: routes.signIn() },
      ],
      Headquarters: [
        { label: '27 E 28th Street, Suite 1754' },
        { label: 'New York, NY 10016' },
      ],
    },
  ];

  const socials = (
    <div className='mt-6 flex justify-start gap-2 md:justify-between md:gap-0'>
      <a href='https://www.instagram.com/nomadhealth/'>
        <InstagramIcon className='text-white' size={30} />
      </a>
      <a href='https://www.facebook.com/nomadhealth/'>
        <FacebookIcon className='text-white' size={30} />
      </a>
      <a href='https://www.tiktok.com/@nomadhealth'>
        <TikTokIcon className='text-white' size={30} />
      </a>
      <a href='https://www.linkedin.com/company/10638650/'>
        <LinkedinIcon className='text-white' size={30} />
      </a>
    </div>
  );

  const storeLinks = (
    <>
      <AppStoreBadgeLink scale={storeBadgeScale} />
      <PlayStoreBadgeLink scale={storeBadgeScale} />
    </>
  );

  const bottomItems: { text: React.ReactNode; href?: string }[][] = [
    [
      { text: 'Privacy Policy', href: '/privacy' },
      { text: 'Terms of Service', href: '/tos' },
      {
        text: 'Do Not Sell or Share My Personal Information',
        href: '/privacy#jurisdiction-specific-notices',
      },
    ],
    [
      { text: <>Copyright &#169; {new Date().getFullYear()} Nomad Health</> },
      { text: 'All Rights Reserved' },
    ],
  ];

  const topFooterTopContent =
    topContent || 'Join *300,000+* clinicians using Nomad today!';
  const topFooterLinkContent = linkContent || 'Join Nomad';
  const topFooterLinkLink = linkUrlPath || routes.register();

  return (
    <footer>
      <div className='relative flex-col bg-primary-light px-8 pb-24 pt-8 md:pb-52 md:pl-40 md:pt-24'>
        <CrossingUpLine className='pointer-events-none absolute bottom-0 left-0 z-10 w-10/12' />
        <Typography.Heading1 className='max-w-2xl'>
          {topFooterTopContent}
        </Typography.Heading1>
        <Link
          href={topFooterLinkLink}
          className='mt-10 max-w-fit'
          variant={LinkVariant.PRIMARY_BUTTON}
        >
          {topFooterLinkContent}
        </Link>
      </div>
      <div className='relative flex w-full flex-col bg-primary-extra-dark px-10 pb-10 pt-14 md:pr-0 md:pt-28'>
        <CrossingDownLine className='pointer-events-none absolute left-[14.82%] top-0 z-10 w-[45.55%]' />
        <div className='flex w-full flex-col md:flex-row'>
          <div className='w-full md:mr-8 md:w-5/12'>
            <div className='max-w-lg'>
              <Typography.Heading1 className='text-primary-contrast'>
                Helping healthcare professionals get where they want to go
                *faster.*
              </Typography.Heading1>
            </div>
            {includeLinks && (
              <div className='mt-10 hidden flex-wrap gap-8 lg:flex'>
                {storeLinks}
              </div>
            )}
          </div>
          <div className='flex w-full flex-col justify-start md:w-7/12 md:flex-row md:gap-8'>
            {(includeLinks ? linkColumns : []).map((column, index) => {
              const isLast = index === linkColumns.length - 1;
              return (
                <div
                  key={index}
                  className='md:first:w-40 md:[&:nth-child(2)]:mt-24'
                >
                  {Object.entries(column).map(([title, links]) => (
                    <div
                      key={title}
                      className='mt-8 flex flex-col md:first:mt-0'
                    >
                      <Typography.Body2
                        as='span'
                        className='mb-2 font-bold uppercase tracking-[.8px] text-primary-contrast'
                      >
                        {title}
                      </Typography.Body2>
                      {links.map(({ label, href, target }) => (
                        <Typography.Body2
                          key={`${label}|${href}`}
                          as='span'
                          className={clsxm(
                            'tracking-[.8px] text-primary-contrast',
                            href && 'hover:text-primary-dark'
                          )}
                        >
                          {href ? (
                            <a
                              href={href}
                              target={target}
                              className='text-inherit no-underline'
                            >
                              {label}
                            </a>
                          ) : (
                            label
                          )}
                        </Typography.Body2>
                      ))}
                    </div>
                  ))}
                  {isLast && socials}
                </div>
              );
            })}
          </div>
        </div>
        <div className='mt-10 flex w-full flex-col gap-12 lg:flex-row lg:gap-0'>
          <a
            className='flex w-full justify-start lg:mr-8 lg:w-5/12'
            href={logoUrl}
          >
            <NomadLogoIcon scale={nomadLogoScale} className='text-white' />
          </a>
          {includeLinks && (
            <div className='flex flex-wrap gap-4 lg:hidden'>{storeLinks}</div>
          )}
          <div className='flex w-full flex-col gap-6 lg:w-7/12 lg:flex-row lg:gap-8'>
            {bottomItems.map((items, i) => (
              <div
                key={i}
                className='flex flex-col gap-2 lg:gap-0 lg:first:w-40'
              >
                {items.map(({ text, href }) => (
                  <Typography.Body3
                    key={`${text}|${href}`}
                    as='span'
                    className={clsxm(
                      'text-primary-contrast',
                      href && 'transition-colors hover:text-primary-dark'
                    )}
                  >
                    {href ? (
                      <a href={href} className='text-inherit no-underline'>
                        {text}
                      </a>
                    ) : (
                      text
                    )}
                  </Typography.Body3>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

function CrossingUpLine({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 1321 398'
      fill='none'
      className={className}
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M-7 309.298C110.018 307.925 188.474 355.208 235.367 398H241.233C194.151 353.935 113.91 303.914 -7 305.328V309.298ZM316.578 398H321.037C333.975 370.887 351.987 338.726 375.771 307.964C427.685 240.818 506.857 180.611 620.657 193.612C722.267 205.219 791.508 281.727 836.65 361.024C843.68 373.373 850.119 385.778 855.998 398H860.45C854.317 385.156 847.568 372.098 840.168 359.099C794.815 279.431 724.678 201.504 621.106 189.672C505.337 176.447 424.926 237.819 372.555 305.555C348.077 337.216 329.67 370.328 316.578 398ZM951.759 398H956.075C968.424 365.767 983.871 330.515 1002.99 294.562C1064.29 179.304 1163.21 57.0882 1318.67 3.87001C1319.72 3.51031 1320.27 2.38028 1319.9 1.3459C1319.53 0.311513 1318.38 -0.23548 1317.32 0.124214C1160.52 53.8058 1060.95 177.005 999.415 292.712C979.933 329.345 964.25 365.25 951.759 398Z'
        className='text-primary'
        fill='currentColor'
      />
    </svg>
  );
}

function CrossingDownLine({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 722 147'
      fill='none'
      className={className}
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M717.137 -0.000854492L721.453 -0.000854492C694.189 71.1333 682.006 127.577 678.703 144.412C678.395 145.983 677.046 146.793 675.854 146.885C674.625 146.979 673.058 146.318 672.661 144.607C668.599 127.117 653.163 66.2385 621.287 -0.000854492L625.739 -0.000854492C655.056 61.3684 670.329 117.843 675.58 139.477C679.838 118.772 692.057 65.7355 717.137 -0.000854492ZM6.4613 -0.000854492C35.2851 26.9521 51.7057 51.6943 57.4403 61.1001C60.4011 51.8676 68.2889 28.8905 81.9649 -0.000854492L86.425 -0.000854492C71.083 32.1346 62.8631 57.1985 60.6032 64.4755C60.1795 65.84 58.9534 66.5054 57.7944 66.4949C56.7127 66.485 55.6415 65.9113 55.0473 64.9029C50.5291 57.2359 33.1563 29.6879 0.592774 -0.000854492H6.4613Z'
        className='text-primary-light'
        fill='currentColor'
      />
    </svg>
  );
}
